import React from 'react';

import { useIntl } from 'react-intl';
import { useHistory, useParams } from 'react-router-dom';

import { notification } from 'antd';

import WorkJourneyService from '@src/services/WorkJourneyService';
import safeExecute from '@src/util/safeExecute';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterContainer from '@src/components/FilterContainer';
import IntlMessages from '@src/util/IntlMessages';

import { useWorkJourneyContext } from '../../contexts';
import { useWorkJourneyValidationTranslations } from '../../hooks/useWorkJourneyValidationTranslations';
import {
  notifyWorkJourneySaveError,
  notifyWorkJourneyValidationErrors,
  runWorkJourneyValidation,
} from '../../utils/submitWorkJourneyForm';
import JourneyFormSelector from './components/JourneyFormSelector';
import JourneyType from './components/JourneyType';

const workJourneyService = new WorkJourneyService();
/**
 * @description View to create a new work journey
 * @returns
 */
function WorkJourneyCreate() {
  const history = useHistory();
  const { orgId } = useParams<{ orgId: string }>();
  const { workJourneyType, setWorkJourneyType, workJourneyForm, setIsSaving, isSaving } =
    useWorkJourneyContext();
  const intl = useIntl();
  const validationTranslations = useWorkJourneyValidationTranslations();

  const getTitle = () => {
    if (workJourneyType) return intl.formatMessage({ id: 'workJourney.createJourney' });
    return intl.formatMessage({ id: 'workJourney.whatTypeCreate' });
  };

  const handleBack = () => {
    if (workJourneyType) setWorkJourneyType(null);
    else history.go(-1);
  };

  const createWorkJourney = async () => {
    if (isSaving) return;

    const validation = runWorkJourneyValidation(workJourneyForm, intl);
    if (!validation.isValid) {
      notifyWorkJourneyValidationErrors(validation.errors, intl);
      return;
    }

    setIsSaving(true);
    safeExecute(async () => {
      const res = await workJourneyService
        .create(workJourneyForm, workJourneyForm.organizationId, validationTranslations)
        .catch((error: unknown) => {
          notifyWorkJourneySaveError(error, intl, {
            members: workJourneyForm.members,
            matchUrl: `/${orgId}/-`,
          });
          return undefined;
        });

      if (res) {
        const message = intl.formatMessage({ id: 'workJourney.createdSuccessfully' });
        notification.success({ message });
        setWorkJourneyType(null);
        setTimeout(() => history.push(`/${orgId}/-/work-journey`), 1000);
      }
    }).finally(() => {
      setIsSaving(false);
    });
  };

  return (
    <BoxContainer>
      <BoxContainer content fixed bottomBorder>
        <FilterContainer
          className="w-100"
          goBack={handleBack}
          title={<Title.Header value={getTitle()} />}
          actionButtons={
            workJourneyType
              ? [
                  {
                    iconName: 'save',
                    label: <IntlMessages id="button.save" />,
                    type: 'primary',
                    action: () => createWorkJourney(),
                    disabled: isSaving,
                  },
                ]
              : []
          }
        />
      </BoxContainer>
      <BoxContainer content>
        {workJourneyType ? <JourneyFormSelector /> : <JourneyType />}
      </BoxContainer>
    </BoxContainer>
  );
}

export default WorkJourneyCreate;
